import React, { useState } from 'react';
import { X, Pipette, Heart, Star, Square, Circle } from 'lucide-react';
import type { ShapeKind, ShapeMeta } from '../hooks/useStamps';
import { renderShapeToSvgDataUrl } from '../utils/stampShapes';
import { useExclusivePopover } from '../hooks/useExclusivePopover';

const COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#8b5cf6', '#ffffff', '#000000'];

interface StampShapePopoverProps {
  /** 提交一個 shape stamp 給父層加入畫面 */
  onSubmit: (dataUrl: string, shapeMeta: ShapeMeta) => void;
  onClose: () => void;
  onRequestEyedropper?: (onPicked: (color: string) => void) => void;
  /** 編輯模式：傳入既有 shape 資料供 prefill */
  initialValues?: ShapeMeta;
  /** popover 展開方向，預設 'left' */
  placement?: 'left' | 'right' | 'bottom';
}

const SHAPES: { kind: ShapeKind; icon: React.FC<{ size?: number; fill?: string }>; label: string }[] = [
  { kind: 'heart', icon: Heart, label: '愛心' },
  { kind: 'star', icon: Star, label: '星星' },
  { kind: 'rect', icon: Square, label: '方形' },
  { kind: 'circle', icon: Circle, label: '圓形' },
];

const StampShapePopover: React.FC<StampShapePopoverProps> = ({
  onSubmit, onClose, onRequestEyedropper, initialValues, placement = 'left',
}) => {
  useExclusivePopover(onClose);
  const [kind, setKind] = useState<ShapeKind>(initialValues?.kind ?? 'heart');
  const isBottom = placement === 'bottom';
  const placementClass =
    placement === 'left' ? 'absolute right-full mr-2 top-0 w-64' :
    placement === 'right' ? 'absolute left-full ml-2 top-0 w-64' :
    'fixed left-4 right-4 bottom-4 mx-auto max-w-md';
  const [color, setColor] = useState(initialValues?.color ?? '#ef4444');
  const [filled, setFilled] = useState(initialValues?.filled ?? true);
  const [lineWidth, setLineWidth] = useState(initialValues?.lineWidth ?? 4);

  const handleSubmit = () => {
    const meta: ShapeMeta = { kind, color, lineWidth, filled };
    const dataUrl = renderShapeToSvgDataUrl(kind, color, lineWidth, filled);
    onSubmit(dataUrl, meta);
    onClose();
  };

  // 預覽：使用相同的 SVG renderer
  const previewDataUrl = renderShapeToSvgDataUrl(kind, color, lineWidth, filled);

  return (
    <>
      {isBottom && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm animate-in fade-in duration-150"
          onClick={onClose}
        />
      )}
    <div
      className={`${placementClass} z-50 ${isBottom ? 'animate-in slide-in-from-bottom-4 duration-200 pt-2' : 'max-w-[calc(100vw-2rem)]'} bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-xl p-3 flex flex-col gap-3`}
      onClick={e => e.stopPropagation()}
    >
      {isBottom && (
        <div className="flex justify-center -mt-1">
          <div className="w-10 h-1 rounded-full bg-slate-300 dark:bg-slate-600" />
        </div>
      )}
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-slate-700 dark:text-slate-200">
          {initialValues ? '編輯印章' : '加入印章'}
        </span>
        <button
          className="text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors"
          onClick={onClose}
        >
          <X size={14} />
        </button>
      </div>

      {/* 形狀選擇 */}
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-slate-400 w-9 shrink-0">形狀</span>
        <div className="flex gap-1">
          {SHAPES.map(({ kind: k, icon: Icon, label }) => (
            <button
              key={k}
              type="button"
              title={label}
              onClick={() => setKind(k)}
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all
                ${kind === k
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}
            >
              <Icon size={16} fill={kind === k ? 'currentColor' : 'none'} />
            </button>
          ))}
        </div>
      </div>

      {/* 樣式：實心 / 框線 */}
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-slate-400 w-9 shrink-0">樣式</span>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => setFilled(true)}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all
              ${filled
                ? 'bg-indigo-600 text-white'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'}`}
          >
            實心
          </button>
          <button
            type="button"
            onClick={() => setFilled(false)}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all
              ${!filled
                ? 'bg-indigo-600 text-white'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'}`}
          >
            框線
          </button>
        </div>
      </div>

      {/* 顏色 */}
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-slate-400 w-9 shrink-0">顏色</span>
        <div className="flex gap-1 items-center flex-wrap">
          {COLORS.map(c => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              className="w-5 h-5 rounded transition-transform hover:scale-110"
              style={{
                background: c,
                boxShadow: color === c
                  ? '0 0 0 2px #6366f1'
                  : c === '#ffffff' ? '0 0 0 1px #94a3b8' : 'none',
              }}
            />
          ))}
          {onRequestEyedropper && (
            <button
              type="button"
              title="從圖片取色"
              onClick={() => onRequestEyedropper(setColor)}
              className="w-5 h-5 flex items-center justify-center rounded text-slate-400 hover:text-indigo-500 transition-colors"
            >
              <Pipette size={13} />
            </button>
          )}
        </div>
      </div>

      {/* 框線粗細（僅框線模式顯示） */}
      {!filled && (
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-400 w-9 shrink-0">粗細</span>
          <input
            type="range"
            min={1}
            max={20}
            value={lineWidth}
            onChange={e => setLineWidth(Number(e.target.value))}
            className="flex-1 accent-indigo-500"
          />
          <span className="text-[10px] text-slate-400 w-5">{lineWidth}</span>
        </div>
      )}

      {/* 預覽 */}
      <div className="flex items-center justify-center px-2 py-3 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
        <img src={previewDataUrl} alt="preview" className="w-12 h-12 object-contain" />
      </div>

      <button
        type="button"
        onClick={handleSubmit}
        className="w-full py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl transition-all"
      >
        {initialValues ? '更新印章' : '加入印章'}
      </button>
    </div>
    </>
  );
};

export default StampShapePopover;
